import { IconMailX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { emailFailReasonKey } from "@/lib/utils/emailFailReason";
import { EmailStatus } from "@/types/enums/EmailStatus";

interface FailReasonBlockProps {
  emailStatus?: EmailStatus | string | null;
  failReason?: string | null;
}

/**
 * Sent-mail detail: SES never took this mail, and why.
 *
 * A FAILED row has no SES message id, so no delivery events — the dialog used
 * to say only "No events available", which read as a missing timeline (board
 * H-14). Same place and shape as SkipReasonBlock: a send-time fact, not a
 * delivery event. Renders nothing for every other status.
 */
export const FailReasonBlock: React.FC<FailReasonBlockProps> = ({
  emailStatus,
  failReason,
}) => {
  const t = useTranslations("pEmailSent");
  const tReason = useTranslations("emailFailReason");

  if (emailStatus !== EmailStatus.FAILED) return null;

  // A known code gets its sentence; an unknown one is printed AS the code
  // (as SkipReasonBlock does); a row written before the consumer stamped a
  // code says so instead of staying silent.
  const key = emailFailReasonKey(failReason);
  const reason = key ? tReason(key) : failReason ? failReason : tReason("none");

  return (
    <div
      data-testid="fail-reason-block"
      className="flex flex-col gap-1 mb-4 border rounded-lg px-4 py-3 surface-secondary outline-quaternary"
    >
      <div className="flex items-center gap-2">
        <IconMailX size={16} className="shrink-0 font-error" />
        <p className="body-2-medium">{t("failedTitle")}</p>
      </div>
      <p className="body-3-regular font-tertiary">
        {t("reason")}: {reason}
      </p>
    </div>
  );
};
